import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import RefreshIcon from '@mui/icons-material/Refresh';
import { getAcceptLanguageHeader } from '../../i18n';

const PAGE_SIZE = 50;
const DEFAULT_MONTHS = 3;
const MAX_MONTHS = 120;

function formatDate(value) {
  if (!value) return '—';
  const date = new Date(String(value).replace(' ', 'T'));
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString();
}

async function responseError(response, fallback) {
  const payload = await response.json().catch(() => ({}));
  return payload?.error || fallback;
}

export default function InactiveCustomersTab({ token, t }) {
  const [periodInput, setPeriodInput] = useState(String(DEFAULT_MONTHS));
  const [appliedMonths, setAppliedMonths] = useState(DEFAULT_MONTHS);
  const [customers, setCustomers] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: PAGE_SIZE, has_more: false });
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const loadCustomers = useCallback(async (page, months) => {
    if (!token) return;
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: String(PAGE_SIZE),
        months: String(months),
      });
      const response = await fetch(`/api/customers/inactive?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Accept-Language': getAcceptLanguageHeader(),
        },
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok || payload?.success === false) {
        throw new Error(payload?.error || t('Ошибка загрузки неактивных клиентов'));
      }

      const data = payload?.data || {};
      setCustomers(Array.isArray(data.items) ? data.items : []);
      setPagination(data.pagination || { page, limit: PAGE_SIZE, has_more: false });
    } catch (requestError) {
      setCustomers([]);
      setPagination({ page, limit: PAGE_SIZE, has_more: false });
      setError(requestError.message || t('Ошибка загрузки неактивных клиентов'));
    } finally {
      setLoading(false);
    }
  }, [t, token]);

  useEffect(() => {
    loadCustomers(1, DEFAULT_MONTHS);
  }, [loadCustomers]);

  const applyPeriod = () => {
    const months = Number(periodInput);
    if (!Number.isInteger(months) || months < 1 || months > MAX_MONTHS) {
      setSnackbar({
        open: true,
        message: t('Введите целое число месяцев от 1 до 120'),
        severity: 'error',
      });
      return;
    }

    setAppliedMonths(months);
    loadCustomers(1, months);
  };

  const downloadCsv = async () => {
    if (!token) return;
    setDownloading(true);
    try {
      const params = new URLSearchParams({ months: String(appliedMonths) });
      const response = await fetch(`/api/customers/inactive/export?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Accept-Language': getAcceptLanguageHeader(),
        },
      });
      if (!response.ok) {
        throw new Error(await responseError(response, t('Ошибка выгрузки CSV')));
      }

      const blob = await response.blob();
      const disposition = response.headers.get('Content-Disposition') || '';
      const filename = disposition.match(/filename="?([^"]+)"?/i)?.[1]
        || `inactive-customers-${appliedMonths}-months.csv`;
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setSnackbar({ open: true, message: t('CSV выгружен'), severity: 'success' });
    } catch (requestError) {
      setSnackbar({
        open: true,
        message: requestError.message || t('Ошибка выгрузки CSV'),
        severity: 'error',
      });
    } finally {
      setDownloading(false);
    }
  };

  const paginationCount = pagination.has_more
    ? (pagination.page || 1) * PAGE_SIZE + 1
    : Math.max(0, ((pagination.page || 1) - 1) * PAGE_SIZE + customers.length);

  return (
    <Box>
      <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
          <TextField
            size="small"
            type="number"
            label={t('Месяцев без успешных заказов')}
            value={periodInput}
            onChange={event => setPeriodInput(event.target.value)}
            onKeyDown={event => {
              if (event.key === 'Enter') applyPeriod();
            }}
            inputProps={{ min: 1, max: MAX_MONTHS, step: 1 }}
            disabled={loading}
            sx={{ minWidth: 270 }}
          />
          <Button
            variant="contained"
            onClick={applyPeriod}
            disabled={loading}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {t('Показать')}
          </Button>
          <Button
            variant="outlined"
            startIcon={downloading ? <CircularProgress size={16} /> : <DownloadIcon />}
            onClick={downloadCsv}
            disabled={downloading || loading}
            sx={{ textTransform: 'none', fontWeight: 600 }}
          >
            {t('Скачать CSV')}
          </Button>
          <Box sx={{ flex: 1 }} />
          <Tooltip title={t('Обновить')}>
            <span>
              <IconButton
                onClick={() => loadCustomers(pagination.page || 1, appliedMonths)}
                disabled={loading}
                sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5 }}
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1.25 }}>
          {t('Показаны клиенты, у которых последний успешный платеж был раньше выбранного периода.')}
        </Typography>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: '#fafafa' }}>
                <TableCell sx={{ fontWeight: 600 }}>{t('Телефон')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('Дата последнего заказа')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('Язык')}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t('Валюта')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : customers.length ? (
                customers.map((customer, index) => (
                  <TableRow key={`${customer.phone}-${index}`} hover>
                    <TableCell>{customer.phone || '—'}</TableCell>
                    <TableCell>{customer.email || '—'}</TableCell>
                    <TableCell>{formatDate(customer.last_order_at)}</TableCell>
                    <TableCell>{customer.language || '—'}</TableCell>
                    <TableCell>{customer.currency || '—'}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                    {t('Неактивные клиенты не найдены')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={paginationCount}
          page={Math.max(0, (pagination.page || 1) - 1)}
          rowsPerPage={PAGE_SIZE}
          rowsPerPageOptions={[PAGE_SIZE]}
          onPageChange={(_, nextPage) => loadCustomers(nextPage + 1, appliedMonths)}
          labelRowsPerPage={t('Строк на странице:')}
        />
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar(current => ({ ...current, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar(current => ({ ...current, open: false }))}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
